import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { ACCESS_AREAS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/AccessAreaColumns";
import { Filter } from "../models/Filter";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from "../models/search/AccessAreaSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteAccessArea, getAccessAreas } from "../services/accessAreaService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { AREA_TYPE }  from "../constants/area-constants";
import { ACCESS_TYPES } from "@/constants/access-constants";
import { removeColumns } from "@/models/utilities/columnHelper";

interface Props {
    setBusy: (isBusy: boolean) => void,
    onFilterChange: (filters: Filter[]) => void,
    getCategoriesTotals: () => void,
    areaType: AREA_TYPE
}

const AreasTab: FC<Props> = ({ setBusy, onFilterChange, getCategoriesTotals, areaType }) => {
    const isAccessArea = areaType === ACCESS_TYPES.ACCESS_AREA
    const itemPath = isAccessArea ?  "/access-control/enrolment/access-area/" : "/access-control/enrolment/lift-area/";
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = ACCESS_AREAS;
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteAccessArea(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        getCategoriesTotals();
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    return (<TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getAccessAreas(areaType, listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={isAccessArea ? removeColumns(columns, ['liftDoor']) : removeColumns(columns, ['doors'])}
        searchDescriptor={searchColumns}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={translate.byKey(isAccessArea ? TranslationKeys.accessAreas : TranslationKeys.liftAreas)}
            selectRows={false}
            rowPath={itemPath} 
            hideAddNewButton={areaType === ACCESS_TYPES.FLOOR_AREA}
            cyName="accessAreas"/>
         </TableContextProvider >
    );
};

export default AreasTab;
